import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import http from '@/services/http'
import { usePermissionsStore } from '@/stores/permissions.store'
import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  Profile,
  RefreshRequest,
  User,
} from '@/types/api'

export const useAuthStore = defineStore(
  'auth',
  () => {
    const accessToken = ref<string | null>(null)
    const refreshToken = ref<string | null>(null)
    const user = ref<User | null>(null)
    const selectedProfile = ref<Profile | null>(null)

    const isAuthenticated = computed(() => !!accessToken.value)
    const userId = computed(() => user.value?.id ?? null)
    const username = computed(() => user.value?.username ?? null)

    const applyLoginPayload = (payload: LoginResponse) => {
      accessToken.value = payload.token ?? null
      refreshToken.value = payload.refresh_token ?? null
      user.value = payload.user ?? null
      if (payload.user?.profiles?.length === 1) {
        selectedProfile.value = payload.user.profiles[0]
      }
      const permissionsStore = usePermissionsStore()
      permissionsStore.set(payload.permissions ?? {})
    }

    async function login(
      usernameInput: string,
      password: string,
      profileSlug?: string,
    ): Promise<LoginResponse> {
      const body: LoginRequest = {
        username: usernameInput,
        password,
        profile: profileSlug,
      }
      const { data } = await http.post<ApiResponse<LoginResponse>>('/auth/login', body)
      const payload = data.data as LoginResponse

      // El backend puede pedir que el usuario elija un perfil antes de emitir el token.
      if (payload.status === 'select_profile') {
        return payload
      }

      applyLoginPayload(payload)
      return payload
    }

    async function refresh(): Promise<string> {
      if (!refreshToken.value) throw new Error('No refresh token available')
      const body: RefreshRequest = { refresh_token: refreshToken.value }
      const { data } = await http.post<ApiResponse<LoginResponse>>('/auth/refresh', body)
      const payload = data.data as LoginResponse
      accessToken.value = payload.token ?? null
      if (payload.refresh_token) refreshToken.value = payload.refresh_token
      return accessToken.value as string
    }

    async function logout(): Promise<void> {
      try {
        await http.post('/auth/logout')
      } finally {
        accessToken.value = null
        refreshToken.value = null
        user.value = null
        selectedProfile.value = null
        const permissionsStore = usePermissionsStore()
        permissionsStore.clear()
      }
    }

    async function fetchMe(): Promise<MeResponse> {
      const { data } = await http.get<ApiResponse<MeResponse>>('/auth/me')
      const payload = data.data as MeResponse
      user.value = payload.user
      const permissionsStore = usePermissionsStore()
      permissionsStore.set(payload.permissions ?? {})
      if (!selectedProfile.value && payload.user.profiles?.length) {
        selectedProfile.value = payload.user.profiles[0]
      }
      return payload
    }

    function setSelectedProfile(profile: Profile | null): void {
      selectedProfile.value = profile
    }

    return {
      // state
      accessToken,
      refreshToken,
      user,
      selectedProfile,
      // getters
      isAuthenticated,
      userId,
      username,
      // actions
      login,
      refresh,
      logout,
      fetchMe,
      setSelectedProfile,
    }
  },
  {
    persist: {
      key: 'tpl.auth',
      pick: ['accessToken', 'refreshToken', 'selectedProfile'],
    },
  },
)
